import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Alert,
  AlertTitle,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { useTranslation } from 'react-i18next';
import { BranchPriceService, BranchPriceItem } from '../services/branchPriceService';
import type { Branch } from '../../inventory/types/inventory.types';

// Her seferde paralel çalışacak istek sayısı — backend'i fazla yormadan
// hızlı toplu kopyalama için iyi bir denge.
const COPY_CHUNK_SIZE = 10;

type CopyStatus = 'idle' | 'preparing' | 'copying' | 'done' | 'error';

interface CopyBranchPricesModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  sourceBranch: Branch | null;        // Şu an görüntülenen (kaynak) şube
  sourcePrices: BranchPriceItem[];    // Halihazırda ekranda yüklü olan fiyatlar
  branches: Branch[];                 // Hedef şube seçimi için tüm şubeler
  userId?: string | null;
  userName?: string | null;
  onSuccess?: () => void;             // Başarılı kopya sonrası (aynı şube seçiliyse liste yenilensin diye)
}

const CopyBranchPricesModal: React.FC<CopyBranchPricesModalProps> = ({
  open,
  onClose,
  companyId,
  sourceBranch,
  sourcePrices,
  branches,
  userId,
  userName,
  onSuccess,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [targetBranch, setTargetBranch] = useState<Branch | null>(null);
  const [targetPrices, setTargetPrices] = useState<BranchPriceItem[]>([]);
  const [loadingTargetPrices, setLoadingTargetPrices] = useState(false);
  const [status, setStatus] = useState<CopyStatus>('idle');
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    created: number;
    skipped: number;
    failed: number;
    failures: Array<{
      inventoryItemId: string;
      productName?: string;
      stockCode?: string;
      status?: number;
      message: string;
    }>;
  } | null>(null);

  const isCopying = status === 'copying' || status === 'preparing';

  // Seçilebilir hedef şubeler (kaynak hariç)
  const targetOptions = useMemo(
    () => branches.filter(b => b.id !== sourceBranch?.id),
    [branches, sourceBranch?.id]
  );

  // Modal her açıldığında / kaynak değiştiğinde state'i resetle
  useEffect(() => {
    if (open) {
      setTargetBranch(null);
      setTargetPrices([]);
      setStatus('idle');
      setProgress({ done: 0, total: 0 });
      setError(null);
      setResult(null);
    }
  }, [open, sourceBranch?.id]);

  // Hedef şube seçildiğinde onun mevcut fiyatlarını çek (skip için)
  useEffect(() => {
    let cancelled = false;
    const fetchTarget = async () => {
      if (!open || !companyId || !targetBranch) {
        setTargetPrices([]);
        return;
      }
      setLoadingTargetPrices(true);
      setError(null);
      try {
        const prices = await BranchPriceService.getBranchPrices(companyId, targetBranch.id);
        if (!cancelled) setTargetPrices(prices);
      } catch (e: any) {
        if (!cancelled) {
          console.error('[CopyBranchPricesModal] Hedef fiyatlar alınamadı:', e);
          setError(tt('Hedef şube fiyatları yüklenemedi.', 'Failed to load target branch prices.'));
          setTargetPrices([]);
        }
      } finally {
        if (!cancelled) setLoadingTargetPrices(false);
      }
    };
    fetchTarget();
    return () => { cancelled = true; };
  }, [open, companyId, targetBranch?.id]);

  // Ön izleme: Yeni eklenecek / atlanacak adet
  const preview = useMemo(() => {
    if (!targetBranch) {
      return { toCopy: [] as BranchPriceItem[], toSkip: [] as BranchPriceItem[] };
    }
    const existingItemIds = new Set(targetPrices.map(p => p.inventoryItemId));
    const toCopy: BranchPriceItem[] = [];
    const toSkip: BranchPriceItem[] = [];
    for (const p of sourcePrices) {
      if (existingItemIds.has(p.inventoryItemId)) {
        toSkip.push(p);
      } else {
        toCopy.push(p);
      }
    }
    return { toCopy, toSkip };
  }, [sourcePrices, targetPrices, targetBranch]);

  const canStart =
    open &&
    !isCopying &&
    !loadingTargetPrices &&
    !!companyId &&
    !!sourceBranch &&
    !!targetBranch &&
    sourcePrices.length > 0 &&
    preview.toCopy.length > 0;

  const handleStartCopy = useCallback(async () => {
    if (!companyId || !sourceBranch || !targetBranch) return;
    const toCopy = preview.toCopy;
    if (toCopy.length === 0) return;

    setStatus('copying');
    setError(null);
    setResult(null);
    setProgress({ done: 0, total: toCopy.length });

    let created = 0;
    let failed = 0;
    const failures: Array<{
      inventoryItemId: string;
      productName?: string;
      stockCode?: string;
      status?: number;
      message: string;
    }> = [];

    // Chunked paralel: her chunk içinde Promise.allSettled ile hata toleransı
    for (let i = 0; i < toCopy.length; i += COPY_CHUNK_SIZE) {
      const chunk = toCopy.slice(i, i + COPY_CHUNK_SIZE);
      const results = await Promise.allSettled(
        chunk.map(src =>
          BranchPriceService.createBranchPrice({
            companyId,
            inventoryItemId: src.inventoryItemId,
            branchId: targetBranch.id,
            branchName: targetBranch.name,
            salePrice: src.salePrice,
            saleCurrency: src.saleCurrency || 'TRY',
            listPrice: src.listPrice,
            listCurrency: src.listCurrency,
            packagePrice: src.packagePrice,
            createdBy: userId || userName || undefined,
          })
        )
      );

      results.forEach((r, idx) => {
        const src = chunk[idx];
        if (r.status === 'fulfilled') {
          created += 1;
        } else {
          failed += 1;
          const err: any = r.reason;
          const status: number | undefined = err?.response?.status;
          // Backend tarafı { message } formatında gönderiyor
          const backendMessage: string | undefined =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message;
          // 404 = ürün silinmiş veya başka şirkete ait (orphan şube fiyat kaydı)
          const friendly = status === 404
            ? tt('Ürün sistemde bulunamadı (silinmiş olabilir).', 'Product not found in the system (may have been deleted).')
            : status === 400
              ? (backendMessage || tt('Geçersiz istek.', 'Invalid request.'))
              : (backendMessage || tt('Bilinmeyen hata.', 'Unknown error.'));
          failures.push({
            inventoryItemId: src.inventoryItemId,
            productName: src.productName,
            stockCode: src.stockCode,
            status,
            message: friendly,
          });
          console.warn('[CopyBranchPricesModal] Kopyalama hatası:', {
            inventoryItemId: src.inventoryItemId,
            status,
            message: backendMessage,
          });
        }
      });
      setProgress(prev => ({ ...prev, done: prev.done + chunk.length }));
    }

    setResult({
      created,
      skipped: preview.toSkip.length,
      failed,
      failures,
    });
    setStatus(failed > 0 && created === 0 ? 'error' : 'done');

    if (created > 0) {
      onSuccess?.();
    }
  }, [companyId, sourceBranch, targetBranch, preview, userId, userName, onSuccess, tt]);

  const progressPct = progress.total > 0
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : 0;

  return (
    <Dialog
      open={open}
      onClose={isCopying ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 5 }}>
        <ContentCopyIcon color="primary" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="div">
            {tt('Şube Fiyatlarını Başka Şubeye Kopyala', 'Copy Branch Prices To Another Branch')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {tt(
              'Sadece hedef şubede fiyatı olmayan ürünler eklenir. Mevcut fiyatlar korunur.',
              'Only products that do not yet have a price in the target branch are added. Existing prices are preserved.'
            )}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isCopying}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Kaynak Şube (readonly) */}
          <TextField
            size="small"
            fullWidth
            label={tt('Kaynak Şube', 'Source Branch')}
            value={sourceBranch?.name || ''}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <StoreIcon color="primary" />
                </InputAdornment>
              ),
            }}
            helperText={tt(
              `${sourcePrices.length} fiyat kaydı kaynak şubeden kopyalanmaya hazır.`,
              `${sourcePrices.length} price records ready to copy from source branch.`
            )}
          />

          {/* Hedef Şube seçimi */}
          <Autocomplete<Branch>
            fullWidth
            size="small"
            options={targetOptions}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={targetBranch}
            disabled={isCopying}
            onChange={(_, v) => setTargetBranch(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={tt('Hedef Şube *', 'Target Branch *')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <StoreIcon color="secondary" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Ön izleme */}
          {targetBranch && (
            <>
              <Divider />
              {loadingTargetPrices ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {tt('Hedef şube fiyatları yükleniyor…', 'Loading target branch prices…')}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {tt('Ön İzleme', 'Preview')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<CheckCircleIcon />}
                      color="success"
                      variant="outlined"
                      label={`${tt('Eklenecek', 'Will add')}: ${preview.toCopy.length}`}
                    />
                    <Chip
                      icon={<SkipNextIcon />}
                      color="warning"
                      variant="outlined"
                      label={`${tt('Atlanacak (zaten var)', 'Will skip (already exists)')}: ${preview.toSkip.length}`}
                    />
                    <Chip
                      variant="outlined"
                      label={`${tt('Hedef şubedeki toplam', 'Target total')}: ${targetPrices.length}`}
                    />
                  </Stack>
                  {preview.toCopy.length === 0 && sourcePrices.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      {tt(
                        'Kaynak şubedeki tüm ürünlerin fiyatı hedef şubede zaten mevcut. Yeni eklenecek kayıt yok.',
                        'All source branch products already have prices in the target branch. Nothing to add.'
                      )}
                    </Alert>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Hata */}
          {error && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {/* İlerleme çubuğu */}
          {status === 'copying' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">
                  {tt('Kopyalanıyor…', 'Copying…')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {progress.done}/{progress.total} ({progressPct}%)
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progressPct} />
            </Box>
          )}

          {/* Sonuç özeti */}
          {result && (status === 'done' || status === 'error') && (
            <Alert severity={result.failed > 0 ? (result.created > 0 ? 'warning' : 'error') : 'success'}>
              <AlertTitle>
                {tt('Kopyalama Tamamlandı', 'Copy Completed')}
              </AlertTitle>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: result.failures.length > 0 ? 1 : 0 }}>
                <Chip size="small" color="success" label={`${tt('Eklendi', 'Added')}: ${result.created}`} />
                <Chip size="small" color="warning" variant="outlined" label={`${tt('Atlandı', 'Skipped')}: ${result.skipped}`} />
                {result.failed > 0 && (
                  <Chip size="small" color="error" label={`${tt('Başarısız', 'Failed')}: ${result.failed}`} />
                )}
              </Stack>

              {result.failures.length > 0 && (
                <Accordion
                  disableGutters
                  square
                  sx={{
                    mt: 1,
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ px: 0, minHeight: 'auto', '& .MuiAccordionSummary-content': { my: 0 } }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      {tt('Başarısız olan ürünler', 'Failed items')} ({result.failures.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    <List dense disablePadding sx={{ maxHeight: 220, overflowY: 'auto' }}>
                      {result.failures.map((f, idx) => (
                        <ListItem key={`${f.inventoryItemId}-${idx}`} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <strong>{f.productName || tt('(Ürün adı yok)', '(No product name)')}</strong>
                                {f.stockCode ? ` · ${f.stockCode}` : ''}
                                {typeof f.status === 'number' ? ` · HTTP ${f.status}` : ''}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {f.message}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {tt(
                        'İpucu: HTTP 404 gelen kayıtlar, ürünü silinmiş olan "yetim" şube fiyat kayıtlarıdır. Bu kayıtlar kaynak şubede hâlâ görünüyor ama hedef şubeye oluşturulamıyor.',
                        'Tip: Records returning HTTP 404 are "orphan" branch-price entries whose product has been deleted. They still appear in the source branch but cannot be created in the target branch.'
                      )}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isCopying}>
          {status === 'done' || status === 'error' ? tt('Kapat', 'Close') : tt('İptal', 'Cancel')}
        </Button>
        <Button
          onClick={handleStartCopy}
          variant="contained"
          color="primary"
          disabled={!canStart}
          startIcon={<ContentCopyIcon />}
        >
          {isCopying
            ? tt('Kopyalanıyor…', 'Copying…')
            : tt(`Kopyala (${preview.toCopy.length})`, `Copy (${preview.toCopy.length})`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyBranchPricesModal;
